import { Navigate, Outlet } from 'react-router-dom';
import { CalendarDays, CalendarRange, Clock, Ellipsis, GraduationCap, KeyRound, Megaphone, UserPlus, Wallet } from 'lucide-react';
import { useAuthStore } from '@/store/auth.store';
import { areaForRole, homeForRole } from '@/lib/roles';
import { FieldLayout, type FieldBarItem } from '@/components/layout/FieldLayout';

/*
  ── What is in the bar, and why ─────────────────────────────────────────────

  The bar holds what a parent opens often; `More` holds what they set once.

  Codes is the reason the app exists and is opened every afternoon. Fees,
  Events, Notices and Results are all things the school tells a parent that
  they act on within a term — fees and notices often, results at the end of it,
  but all four are time-bound and all four are short labels.

  Delegates and History are not. Delegates is configured once, when somebody
  else starts collecting, and History answers a question the pickup SMS has
  usually already answered. Those two were costing the bar the width that made
  "Delegates" truncate at phone size, which is the tell that a bottom bar has
  stopped being a bar and become a list.

  Nothing was removed and no URL changed.
*/
const TABS: FieldBarItem[] = [
  { label: 'Codes', to: '/parent', icon: KeyRound, end: true },
  { label: 'Fees', to: '/parent/fees', icon: Wallet },
  { label: 'Events', to: '/parent/events', icon: CalendarRange },
  { label: 'Notices', to: '/parent/notices', icon: Megaphone },
  { label: 'Results', to: '/parent/results', icon: GraduationCap },
  {
    label: 'More',
    icon: Ellipsis,
    items: [
      { label: 'Timetable', to: '/parent/timetable', icon: CalendarDays, hint: "The week your child's class runs to" },
      { label: 'Delegates', to: '/parent/delegates', icon: UserPlus, hint: 'Who else may collect your child' },
      { label: 'Pickup history', to: '/parent/history', icon: Clock, hint: 'Every collection, with the time' },
    ],
  },
];

export function ParentLayout() {
  const { isAuthenticated, user } = useAuthStore();

  if (!isAuthenticated) return <Navigate to="/parent/login" replace />;

  /* A gate officer or admin who reaches a /parent URL is sent to their own app
     rather than shown empty screens — the parent endpoints would 403 for them. */
  if (areaForRole(user?.role) !== 'parent') return <Navigate to={homeForRole(user?.role)} replace />;

  return (
    <FieldLayout appLabel="Parent" tabs={TABS}>
      <Outlet />
    </FieldLayout>
  );
}
